import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Card, SectionHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import {
  ArrowLeft,
  Stethoscope,
  UtensilsCrossed,
  Code2,
  Building2,
  GraduationCap,
  ShoppingBag,
  Plus,
  Lightbulb,
  Eye,
  EyeOff,
} from 'lucide-react';
import clsx from 'clsx';

/**
 * Static reference page — no API calls, no persistence. The intent is to give
 * a first-time user a concrete mental model of what to put in a custom object
 * type by showing 6 industries' worth of pre-designed blueprints. Each
 * blueprint lists the *types* (data tables) the business would create, and
 * for each type the *fields* with their type, "required" + "AI context"
 * recommendations.
 *
 * If we ever want to convert these to one-click installable templates, the
 * blueprint shape lines up with `customObjectsService.installTemplate()`.
 */

type FieldExample = {
  label: string;
  key: string;
  type: string;
  required?: boolean;
  ai?: boolean;
  hint?: string;
};

type TypeExample = {
  label: string;
  pluralLabel: string;
  key: string;
  icon: string; // Lucide icon name (the editor accepts a string)
  description: string;
  linkToContact: boolean;
  linkToLead?: boolean;
  fields: FieldExample[];
  sampleRecords?: string[];
};

type Industry = {
  id: string;
  name: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  blurb: string;
  whatToTrack: string;
  types: TypeExample[];
};

const INDUSTRIES: Industry[] = [
  // ─── Medical clinic / doctor ──────────────────────────────────────────────
  {
    id: 'clinic',
    name: 'Medical Clinic',
    Icon: Stethoscope,
    blurb: 'Doctors, patients, appointments, treatments — the core data of a multi-doctor practice.',
    whatToTrack:
      'Patients ask about specialties, fees, available days, and book appointments. The AI replies on WhatsApp using the data you put here.',
    types: [
      {
        label: 'Doctor',
        pluralLabel: 'Doctors',
        key: 'doctor',
        icon: 'Stethoscope',
        description: 'Practising doctors at the clinic. Used by the AI to answer "do you have a cardiologist?" and "how much is a consult?".',
        linkToContact: true,
        fields: [
          { label: 'Full name', key: 'full_name', type: 'Text', required: true, ai: true },
          { label: 'Specialty', key: 'specialty', type: 'Select (one)', required: true, ai: true, hint: 'Cardiologist, Dermatologist, Pediatrician, GP, etc.' },
          { label: 'Years experience', key: 'years_experience', type: 'Number', ai: true },
          { label: 'Consult fee', key: 'consult_fee', type: 'Money', required: true, ai: true },
          { label: 'Available days', key: 'available_days', type: 'Select (multiple)', ai: true, hint: 'Mon, Tue, Wed, Thu, Fri, Sat, Sun' },
          { label: 'License number', key: 'license_number', type: 'Text', required: true, hint: 'Internal — do NOT tick AI context' },
          { label: 'Bio', key: 'bio', type: 'Long text', ai: true },
        ],
        sampleRecords: ['Dr. Aisha Khan — Cardiologist', 'Dr. Omar Saleh — Pediatrician'],
      },
      {
        label: 'Treatment',
        pluralLabel: 'Treatments',
        key: 'treatment',
        icon: 'HeartPulse',
        description: 'Procedures and treatments offered. Includes price and duration so AI can quote.',
        linkToContact: false,
        fields: [
          { label: 'Treatment name', key: 'name', type: 'Text', required: true, ai: true },
          { label: 'Category', key: 'category', type: 'Select (one)', ai: true, hint: 'Consultation, Diagnostic, Surgical, Cosmetic, Therapy' },
          { label: 'Price', key: 'price', type: 'Money', required: true, ai: true },
          { label: 'Duration (minutes)', key: 'duration_min', type: 'Number', ai: true },
          { label: 'Description', key: 'description', type: 'Long text', ai: true },
        ],
      },
      {
        label: 'Appointment',
        pluralLabel: 'Appointments',
        key: 'appointment',
        icon: 'CalendarClock',
        description: 'Scheduled patient visits. Linked to the patient (Contact).',
        linkToContact: true,
        fields: [
          { label: 'Doctor', key: 'doctor_name', type: 'Text', required: true, ai: true },
          { label: 'Scheduled at', key: 'scheduled_at', type: 'Date + time', required: true, ai: true },
          { label: 'Status', key: 'status', type: 'Select (one)', required: true, ai: true, hint: 'Scheduled, Confirmed, Completed, Cancelled, No-show' },
          { label: 'Reason for visit', key: 'reason', type: 'Long text', ai: true },
          { label: 'Internal notes', key: 'internal_notes', type: 'Long text', hint: 'Not visible to AI' },
        ],
      },
    ],
  },

  // ─── Restaurant ───────────────────────────────────────────────────────────
  {
    id: 'restaurant',
    name: 'Restaurant',
    Icon: UtensilsCrossed,
    blurb: 'Menu items, daily specials, reservations, delivery zones.',
    whatToTrack:
      'Diners ask "is the pasta vegan?", "what time do you close?", "how much is delivery?". Put answers in your menu + delivery records.',
    types: [
      {
        label: 'Menu item',
        pluralLabel: 'Menu Items',
        key: 'menu_item',
        icon: 'UtensilsCrossed',
        description: 'Dishes and drinks on the menu. AI uses this to answer pricing, allergens, and availability questions.',
        linkToContact: false,
        fields: [
          { label: 'Name', key: 'name', type: 'Text', required: true, ai: true },
          { label: 'Category', key: 'category', type: 'Select (one)', required: true, ai: true, hint: 'Starter, Main, Dessert, Beverage, Side' },
          { label: 'Price', key: 'price', type: 'Money', required: true, ai: true },
          { label: 'Description', key: 'description', type: 'Long text', ai: true },
          { label: 'Allergens', key: 'allergens', type: 'Select (multiple)', ai: true, hint: 'Gluten, Dairy, Nuts, Shellfish, Egg, Soy' },
          { label: 'Vegetarian', key: 'is_vegetarian', type: 'Yes / no', ai: true },
          { label: 'Vegan', key: 'is_vegan', type: 'Yes / no', ai: true },
          { label: 'Spice level', key: 'spice_level', type: 'Select (one)', ai: true, hint: 'Mild, Medium, Hot, Extra Hot' },
          { label: 'Available now', key: 'is_available', type: 'Yes / no', required: true, ai: true },
          { label: 'Image URL', key: 'image_url', type: 'URL', ai: true },
        ],
        sampleRecords: ['Margherita Pizza — AED 45', 'Lamb Biryani — AED 65'],
      },
      {
        label: 'Daily special',
        pluralLabel: 'Daily Specials',
        key: 'daily_special',
        icon: 'Sparkles',
        description: 'Limited-time offers, weekday deals, seasonal menus.',
        linkToContact: false,
        fields: [
          { label: 'Name', key: 'name', type: 'Text', required: true, ai: true },
          { label: 'Day', key: 'day', type: 'Select (one)', ai: true, hint: 'Mon, Tue, Wed, Thu, Fri, Sat, Sun, Daily' },
          { label: 'Time window', key: 'time_window', type: 'Text', ai: true, hint: '"6pm–9pm" or "Lunch 12–3"' },
          { label: 'Discounted price', key: 'price', type: 'Money', ai: true },
          { label: 'Description', key: 'description', type: 'Long text', ai: true },
          { label: 'Active until', key: 'active_until', type: 'Date', ai: true },
        ],
      },
      {
        label: 'Reservation',
        pluralLabel: 'Reservations',
        key: 'reservation',
        icon: 'CalendarCheck',
        description: 'Table bookings made by customers.',
        linkToContact: true,
        fields: [
          { label: 'Date + time', key: 'when', type: 'Date + time', required: true, ai: true },
          { label: 'Party size', key: 'party_size', type: 'Number', required: true, ai: true },
          { label: 'Status', key: 'status', type: 'Select (one)', required: true, ai: true, hint: 'Pending, Confirmed, Seated, Cancelled, No-show' },
          { label: 'Special request', key: 'special_request', type: 'Long text', ai: true },
          { label: 'Table number', key: 'table_number', type: 'Text', hint: 'Internal' },
        ],
      },
      {
        label: 'Delivery zone',
        pluralLabel: 'Delivery Zones',
        key: 'delivery_zone',
        icon: 'Map',
        description: 'Areas you deliver to and the fee for each.',
        linkToContact: false,
        fields: [
          { label: 'Zone name', key: 'name', type: 'Text', required: true, ai: true, hint: '"Marina", "JBR", "Downtown"' },
          { label: 'Delivery fee', key: 'fee', type: 'Money', required: true, ai: true },
          { label: 'Est. delivery time', key: 'eta_min', type: 'Number', ai: true, hint: 'Minutes' },
          { label: 'Min order', key: 'min_order', type: 'Money', ai: true },
        ],
      },
    ],
  },

  // ─── Software house ───────────────────────────────────────────────────────
  {
    id: 'software_house',
    name: 'Software House',
    Icon: Code2,
    blurb: 'Services you offer, tech stack, projects, case studies, retainers.',
    whatToTrack:
      'Prospects ask "how much for an MVP?", "do you build with Flutter?", "show me an e-commerce app you built". The AI quotes from these records.',
    types: [
      {
        label: 'Service Package',
        pluralLabel: 'Service Packages',
        key: 'service_package',
        icon: 'Package',
        description: 'Productized services — MVP, web app, mobile app, AI integration, etc. With scope, timeline, and starting price.',
        linkToContact: false,
        fields: [
          { label: 'Name', key: 'name', type: 'Text', required: true, ai: true, hint: '"MVP in 6 weeks"' },
          { label: 'Category', key: 'category', type: 'Select (one)', required: true, ai: true, hint: 'Web App, Mobile App, MVP, AI, E-commerce, SaaS, Maintenance, UI/UX, DevOps' },
          { label: 'Short pitch', key: 'short_pitch', type: 'Text', required: true, ai: true },
          { label: 'Description', key: 'description', type: 'Long text', required: true, ai: true },
          { label: 'Starting price', key: 'starting_price', type: 'Money', required: true, ai: true },
          { label: 'Currency', key: 'currency', type: 'Select (one)', required: true, ai: true, hint: 'USD, AED, EUR, PKR, GBP' },
          { label: 'Typical timeline', key: 'typical_timeline', type: 'Text', required: true, ai: true, hint: '"4–6 weeks"' },
          { label: 'Tech stack', key: 'tech_stack', type: 'Select (multiple)', ai: true },
          { label: 'Deliverables', key: 'deliverables', type: 'Long text', ai: true },
          { label: 'Internal margin %', key: 'margin_pct', type: 'Number', hint: 'Internal — do NOT tick AI context' },
        ],
        sampleRecords: ['MVP in 6 weeks — $4,500', 'Flutter Mobile App — $12,000'],
      },
      {
        label: 'Tech Capability',
        pluralLabel: 'Tech Capabilities',
        key: 'tech_capability',
        icon: 'Cpu',
        description: 'Technologies you have proven delivery experience in. AI uses this to answer "do you do X?" honestly.',
        linkToContact: false,
        fields: [
          { label: 'Name', key: 'name', type: 'Text', required: true, ai: true, hint: '"React Native", "PostgreSQL", "AWS"' },
          { label: 'Category', key: 'category', type: 'Select (one)', required: true, ai: true, hint: 'Frontend, Backend, Mobile, Database, Cloud, DevOps, AI/ML, Auth' },
          { label: 'Proficiency', key: 'proficiency', type: 'Select (one)', required: true, ai: true, hint: 'Expert, Strong, Working, Exploring' },
          { label: 'Years used', key: 'years_used', type: 'Number', ai: true },
          { label: 'Projects shipped', key: 'projects_shipped', type: 'Number', ai: true },
          { label: 'Notes', key: 'notes', type: 'Long text', ai: true },
        ],
      },
      {
        label: 'Case Study',
        pluralLabel: 'Case Studies',
        key: 'case_study',
        icon: 'Briefcase',
        description: 'Completed client projects with public-facing summary. Social proof for sales.',
        linkToContact: false,
        fields: [
          { label: 'Project name', key: 'project_name', type: 'Text', required: true, ai: true },
          { label: 'Client', key: 'client_name', type: 'Text', ai: true },
          { label: 'Industry', key: 'industry', type: 'Select (one)', required: true, ai: true, hint: 'Fintech, Healthcare, E-commerce, EdTech, Real Estate, Logistics' },
          { label: 'Summary', key: 'summary', type: 'Long text', required: true, ai: true },
          { label: 'Outcome', key: 'outcome', type: 'Long text', required: true, ai: true, hint: '"20k MAU in 3 months"' },
          { label: 'Tech used', key: 'tech_used', type: 'Select (multiple)', ai: true },
          { label: 'Public link', key: 'public_link', type: 'URL', ai: true },
          { label: 'Featured', key: 'is_featured', type: 'Yes / no', ai: true },
        ],
      },
      {
        label: 'Project',
        pluralLabel: 'Projects',
        key: 'project',
        icon: 'FolderKanban',
        description: 'Active and delivered client engagements.',
        linkToContact: true,
        linkToLead: true,
        fields: [
          { label: 'Project name', key: 'name', type: 'Text', required: true, ai: true },
          { label: 'Type', key: 'type', type: 'Select (one)', required: true, ai: true },
          { label: 'Status', key: 'status', type: 'Select (one)', required: true, ai: true, hint: 'Discovery, In Progress, In QA, Delivered, On Hold, Cancelled' },
          { label: 'Start date', key: 'start_date', type: 'Date', ai: true },
          { label: 'Target delivery', key: 'target_delivery', type: 'Date', ai: true },
          { label: 'Contract value', key: 'contract_value', type: 'Money', hint: 'Internal — do NOT expose' },
          { label: 'Repository URL', key: 'repo_url', type: 'URL', hint: 'Internal' },
        ],
      },
    ],
  },

  // ─── Real estate ──────────────────────────────────────────────────────────
  {
    id: 'real_estate',
    name: 'Real Estate Agency',
    Icon: Building2,
    blurb: 'Listings, viewings, offers, agents.',
    whatToTrack:
      'Buyers and tenants ask "any 2-bed in Marina under 2M?", "schedule a viewing", "what\'s the price per sqft?". Listings are your inventory.',
    types: [
      {
        label: 'Listing',
        pluralLabel: 'Listings',
        key: 'listing',
        icon: 'Home',
        description: 'Properties for sale or rent. The AI matches incoming queries against this inventory.',
        linkToContact: false,
        fields: [
          { label: 'Title', key: 'title', type: 'Text', required: true, ai: true },
          { label: 'Listing type', key: 'listing_type', type: 'Select (one)', required: true, ai: true, hint: 'Sale, Rent (yearly), Rent (monthly), Short-term' },
          { label: 'Property type', key: 'property_type', type: 'Select (one)', required: true, ai: true, hint: 'Apartment, Villa, Townhouse, Studio, Office, Warehouse' },
          { label: 'Bedrooms', key: 'bedrooms', type: 'Number', ai: true },
          { label: 'Bathrooms', key: 'bathrooms', type: 'Number', ai: true },
          { label: 'Area (sqft)', key: 'area_sqft', type: 'Number', ai: true },
          { label: 'Price', key: 'price', type: 'Money', required: true, ai: true },
          { label: 'Currency', key: 'currency', type: 'Select (one)', required: true, ai: true },
          { label: 'Location / community', key: 'location', type: 'Text', required: true, ai: true },
          { label: 'Furnished', key: 'is_furnished', type: 'Yes / no', ai: true },
          { label: 'Features', key: 'features', type: 'Select (multiple)', ai: true, hint: 'Pool, Gym, Parking, Maid Room, Sea View, etc.' },
          { label: 'Status', key: 'status', type: 'Select (one)', required: true, ai: true, hint: 'Available, Reserved, Sold, Rented, Off-market' },
          { label: 'Listing URL', key: 'listing_url', type: 'URL', ai: true },
          { label: 'Internal commission %', key: 'commission_pct', type: 'Number', hint: 'Internal' },
        ],
        sampleRecords: ['2BR Marina · AED 1.85M · Sale', 'Studio JBR · AED 60k/yr · Rent'],
      },
      {
        label: 'Viewing',
        pluralLabel: 'Viewings',
        key: 'viewing',
        icon: 'CalendarSearch',
        description: 'Scheduled property viewings with prospects.',
        linkToContact: true,
        linkToLead: true,
        fields: [
          { label: 'Listing reference', key: 'listing_ref', type: 'Text', required: true, ai: true },
          { label: 'Scheduled at', key: 'scheduled_at', type: 'Date + time', required: true, ai: true },
          { label: 'Status', key: 'status', type: 'Select (one)', required: true, ai: true, hint: 'Scheduled, Done, No-show, Rescheduled, Cancelled' },
          { label: 'Agent', key: 'agent_name', type: 'Text', ai: true },
          { label: 'Outcome', key: 'outcome', type: 'Long text', ai: true, hint: '"Liked it, asking for negotiation"' },
        ],
      },
    ],
  },

  // ─── Education / school ───────────────────────────────────────────────────
  {
    id: 'education',
    name: 'School / Tutoring',
    Icon: GraduationCap,
    blurb: 'Courses, classes, instructors, enrollments.',
    whatToTrack:
      'Parents and students ask "what classes do you offer for grade 8?", "how much per term?", "who teaches algebra?". Courses + instructors answer most of it.',
    types: [
      {
        label: 'Course',
        pluralLabel: 'Courses',
        key: 'course',
        icon: 'BookOpen',
        description: 'Subjects or programmes you offer, with pricing and grade level.',
        linkToContact: false,
        fields: [
          { label: 'Course name', key: 'name', type: 'Text', required: true, ai: true },
          { label: 'Subject', key: 'subject', type: 'Select (one)', required: true, ai: true, hint: 'Maths, Physics, Chemistry, English, Coding, Music, Art' },
          { label: 'Grade / level', key: 'grade', type: 'Select (one)', required: true, ai: true, hint: 'Primary, KS3, KS4, A-Level, IB, Adult' },
          { label: 'Format', key: 'format', type: 'Select (one)', required: true, ai: true, hint: 'In-person, Online, Hybrid' },
          { label: 'Duration (weeks)', key: 'duration_weeks', type: 'Number', ai: true },
          { label: 'Price', key: 'price', type: 'Money', required: true, ai: true },
          { label: 'Currency', key: 'currency', type: 'Select (one)', required: true, ai: true },
          { label: 'Description', key: 'description', type: 'Long text', ai: true },
          { label: 'Currently open for enrolment', key: 'is_open', type: 'Yes / no', required: true, ai: true },
        ],
      },
      {
        label: 'Instructor',
        pluralLabel: 'Instructors',
        key: 'instructor',
        icon: 'UserCheck',
        description: 'Teachers and tutors. Linked to Contact.',
        linkToContact: true,
        fields: [
          { label: 'Full name', key: 'full_name', type: 'Text', required: true, ai: true },
          { label: 'Subjects', key: 'subjects', type: 'Select (multiple)', required: true, ai: true },
          { label: 'Years teaching', key: 'years_teaching', type: 'Number', ai: true },
          { label: 'Qualifications', key: 'qualifications', type: 'Long text', ai: true },
          { label: 'Hourly rate', key: 'hourly_rate', type: 'Money', hint: 'Internal' },
        ],
      },
      {
        label: 'Enrolment',
        pluralLabel: 'Enrolments',
        key: 'enrolment',
        icon: 'ClipboardCheck',
        description: 'Active student enrolments per course.',
        linkToContact: true,
        fields: [
          { label: 'Course', key: 'course_name', type: 'Text', required: true, ai: true },
          { label: 'Start date', key: 'start_date', type: 'Date', required: true, ai: true },
          { label: 'Status', key: 'status', type: 'Select (one)', required: true, ai: true, hint: 'Active, Paused, Completed, Withdrawn' },
          { label: 'Fee paid', key: 'fee_paid', type: 'Money', hint: 'Internal' },
        ],
      },
    ],
  },

  // ─── E-commerce ───────────────────────────────────────────────────────────
  {
    id: 'ecommerce',
    name: 'E-commerce Store',
    Icon: ShoppingBag,
    blurb: 'Products, collections, orders, promotions.',
    whatToTrack:
      'Shoppers ask "is this in stock?", "what sizes?", "any discount code?". Product + Promotion records cover 80% of pre-purchase questions.',
    types: [
      {
        label: 'Product',
        pluralLabel: 'Products',
        key: 'product',
        icon: 'ShoppingBag',
        description: 'Catalog items. AI uses these to answer pricing, sizing, availability.',
        linkToContact: false,
        fields: [
          { label: 'Name', key: 'name', type: 'Text', required: true, ai: true },
          { label: 'SKU', key: 'sku', type: 'Text', required: true, ai: true },
          { label: 'Category', key: 'category', type: 'Select (one)', required: true, ai: true },
          { label: 'Price', key: 'price', type: 'Money', required: true, ai: true },
          { label: 'Sale price', key: 'sale_price', type: 'Money', ai: true },
          { label: 'In stock', key: 'in_stock', type: 'Yes / no', required: true, ai: true },
          { label: 'Stock count', key: 'stock_count', type: 'Number', ai: true },
          { label: 'Available sizes', key: 'sizes', type: 'Select (multiple)', ai: true },
          { label: 'Available colours', key: 'colours', type: 'Select (multiple)', ai: true },
          { label: 'Description', key: 'description', type: 'Long text', ai: true },
          { label: 'Image URL', key: 'image_url', type: 'URL', ai: true },
          { label: 'Product URL', key: 'product_url', type: 'URL', ai: true },
          { label: 'Cost price', key: 'cost_price', type: 'Money', hint: 'Internal margin data' },
        ],
        sampleRecords: ['Cotton T-Shirt — AED 79', 'Linen Trousers — AED 199'],
      },
      {
        label: 'Promotion',
        pluralLabel: 'Promotions',
        key: 'promotion',
        icon: 'Tag',
        description: 'Active discount codes and sales.',
        linkToContact: false,
        fields: [
          { label: 'Code', key: 'code', type: 'Text', required: true, ai: true },
          { label: 'Discount %', key: 'discount_pct', type: 'Number', ai: true },
          { label: 'Discount amount', key: 'discount_amount', type: 'Money', ai: true },
          { label: 'Min spend', key: 'min_spend', type: 'Money', ai: true },
          { label: 'Active from', key: 'active_from', type: 'Date', ai: true },
          { label: 'Active until', key: 'active_until', type: 'Date', required: true, ai: true },
          { label: 'Active', key: 'is_active', type: 'Yes / no', required: true, ai: true },
          { label: 'Description', key: 'description', type: 'Long text', ai: true },
        ],
      },
    ],
  },
];

const inputCls =
  'rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary';

export default function CustomObjectExamples() {
  const [activeId, setActiveId] = useState<string>(INDUSTRIES[0].id);
  const [showAiOnly, setShowAiOnly] = useState(false);
  const active = INDUSTRIES.find((i) => i.id === activeId)!;

  return (
    <>
      <Topbar
        title="Examples — what to put in a Custom Object Type"
        breadcrumb="Data · Custom Objects"
        description="Pre-designed blueprints for common businesses. Use them as a recipe — copy the structure, adjust to your reality."
      />

      <div className="px-7 py-7 w-full space-y-6">
        {/* Back to types list */}
        <Link
          to="/custom-objects"
          className="inline-flex items-center gap-2 text-sm font-medium text-text-secondary hover:text-primary"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface hover:border-primary/50">
            <ArrowLeft size={14} />
          </span>
          Back to Custom Objects
        </Link>

        {/* Intro callout */}
        <Card padded>
          <div className="flex gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <Lightbulb size={18} />
            </div>
            <div className="space-y-1.5 text-sm text-text-secondary">
              <p>
                <strong className="text-text-primary">A "type" is a data table you design.</strong> A "record" is one row.
                e.g. you create a <em>Service Package</em> type, then add records like
                "MVP in 6 weeks — $4,500" and "Flutter App — $12,000".
              </p>
              <p>
                Pick an industry below to see the full set of types you'd typically create, with sample fields
                and which ones to mark <strong>Include in AI context</strong> so the WhatsApp / IG bot can
                quote from them.
              </p>
            </div>
          </div>
        </Card>

        {/* Industry tabs */}
        <Card padded>
          <SectionHeader
            title="Pick a business"
            subtitle="Each industry shows the types you'd create and what fields each one should have."
            action={
              <Button
                size="sm"
                variant="ghost"
                iconLeft={showAiOnly ? <EyeOff size={12} /> : <Eye size={12} />}
                onClick={() => setShowAiOnly((v) => !v)}
              >
                {showAiOnly ? 'Show all fields' : 'Show only AI-context fields'}
              </Button>
            }
          />
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {INDUSTRIES.map((ind) => {
              const Icon = ind.Icon;
              const isActive = ind.id === activeId;
              return (
                <button
                  key={ind.id}
                  onClick={() => setActiveId(ind.id)}
                  className={clsx(
                    'flex shrink-0 items-center gap-2 rounded-xl border px-4 py-2.5 text-sm transition-colors',
                    isActive
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-border bg-surface hover:border-primary/40',
                  )}
                >
                  <Icon size={16} />
                  <span className="font-semibold">{ind.name}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-4 rounded-xl border border-border bg-surface-alt/40 p-4 text-sm text-text-secondary">
            <div className="flex items-start gap-3">
              <active.Icon size={18} className="mt-0.5 shrink-0 text-primary" />
              <div>
                <div className="font-display text-sm font-semibold text-text-primary">{active.name}</div>
                <p className="mt-1 text-xs">{active.blurb}</p>
                <p className="mt-1 text-xs italic text-text-muted">{active.whatToTrack}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Types for active industry */}
        {active.types.map((ty, i) => (
          <Card key={ty.key} padded>
            <SectionHeader
              title={`Type ${i + 1}: ${ty.label}`}
              subtitle={ty.description}
              action={
                <Link
                  to="/custom-objects/types/new"
                  className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-surface-alt"
                >
                  <Plus size={12} />
                  Create one like this
                </Link>
              }
            />

            {/* Basics summary strip */}
            <div className="mb-4 grid gap-2 rounded-xl border border-border bg-surface-alt/40 p-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
              <KV k="Label" v={ty.label} />
              <KV k="Plural" v={ty.pluralLabel} />
              <KV k="Key" v={ty.key} mono />
              <KV k="Icon" v={ty.icon} mono />
              <KV k="Linked to Contact" v={ty.linkToContact ? 'Yes' : 'No'} />
              <KV k="Linked to Lead" v={ty.linkToLead ? 'Yes' : 'No'} />
            </div>

            {/* Fields table */}
            <div className="overflow-hidden rounded-xl border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-surface-alt/60 text-left text-[10px] uppercase tracking-wider text-text-muted">
                    <th className="px-3 py-2 font-semibold">Label</th>
                    <th className="px-3 py-2 font-semibold">Key</th>
                    <th className="px-3 py-2 font-semibold">Type</th>
                    <th className="px-3 py-2 text-center font-semibold">Required</th>
                    <th className="px-3 py-2 text-center font-semibold">AI ctx</th>
                    <th className="px-3 py-2 font-semibold">Hint</th>
                  </tr>
                </thead>
                <tbody>
                  {ty.fields
                    .filter((f) => !showAiOnly || f.ai)
                    .map((f) => (
                      <tr key={f.key} className="border-t border-border">
                        <td className="px-3 py-2 font-medium text-text-primary">{f.label}</td>
                        <td className="px-3 py-2 font-mono text-text-muted">{f.key}</td>
                        <td className="px-3 py-2 text-text-secondary">{f.type}</td>
                        <td className="px-3 py-2 text-center">{f.required ? '✓' : '—'}</td>
                        <td className="px-3 py-2 text-center">
                          {f.ai ? (
                            <span className="rounded-full bg-success-soft px-1.5 py-0.5 text-2xs text-success">✓</span>
                          ) : (
                            <span className="text-text-muted">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-text-muted">{f.hint ?? ''}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Sample records hint */}
            {ty.sampleRecords && ty.sampleRecords.length > 0 && (
              <div className="mt-3 rounded-xl border border-dashed border-border bg-surface-alt/30 p-3 text-xs text-text-secondary">
                <span className="font-semibold text-text-primary">After creating this type, add records like: </span>
                {ty.sampleRecords.join(' · ')}
              </div>
            )}
          </Card>
        ))}

        {/* Footer CTA */}
        <Card padded>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-text-secondary">
              Ready to build your own? Use any of the blueprints above as a starting point.
            </div>
            <Link
              to="/custom-objects/types/new"
              className={clsx(inputCls, 'inline-flex items-center gap-2 bg-primary text-text-on-primary hover:bg-primary-hover')}
            >
              <Plus size={14} />
              Create custom object type
            </Link>
          </div>
        </Card>
      </div>
    </>
  );
}

const KV: React.FC<{ k: string; v: string; mono?: boolean }> = ({ k, v, mono }) => (
  <div className="flex items-baseline gap-2">
    <span className="text-text-muted">{k}:</span>
    <span className={clsx('text-text-primary', mono && 'font-mono')}>{v}</span>
  </div>
);
